import React from 'react';
import { Box, Container, Divider, Typography } from '@mui/material';
import { useI18nContext } from '@procore/core-react';
import { useStyles } from './ProjectCard.styles';
import colors from '../../styles/colors';
import LocationIcon from '../../components/Icons/LocationIcon';
import CalendarIcon from '../Icons/CalendarIcon';
import BuildingIcon from '../../components/Icons/BuildingIcon';
import { type ProjectTile } from '../../types/project';
import moment from 'moment';
import EmailObfuscationWrapper from '../utils/EmailObfuscationWrapper';
import ProjectMetaData from '../Project/ProjectMetaData';
import {
  buildProjectLocationText,
  getProjectSectorLabel,
  getBidDueStatus,
} from '../../helpers/build-texts';
import PaymentsIcon from '../Icons/PaymentsIcon';
import { capitalizeFirstLetterOfWords } from '../../helpers/capitalize-first-letter-of-words';
import {
  getNdaProjectDisplay,
  resolveProjectTitle,
} from '../../helpers/nda-display';

const ProjectCard = ({ project }: { project: ProjectTile }) => {
  const I18n = useI18nContext();
  const classes = useStyles();
  const projectAddressText = buildProjectLocationText(project?.projectLocation);

  const { status } = getBidDueStatus(project);

  const ndaDisplay = getNdaProjectDisplay(project);
  const projectTitle = resolveProjectTitle(
    project,
    ndaDisplay,
    I18n.t('components.breadcrumbs.nda_project_title')
  );
  const formattedSector =
    ndaDisplay.showSector && project?.sector
      ? getProjectSectorLabel(project?.sector, I18n.t.bind(I18n))
      : '';
  const fundingType = ndaDisplay.showFundingType ? project?.fundingType : null;

  return (
    <Container className={classes.container}>
      <Box className={classes.projectNameContainer}>
        <Typography
          variant="h1"
          style={{
            minHeight: '28px',
            fontSize: '20px',
            lineHeight: '28px',
          }}
          data-test-id="bid-package-name"
        >
          <EmailObfuscationWrapper>{projectTitle}</EmailObfuscationWrapper>
        </Typography>
        <Typography
          variant="h2"
          style={{
            fontSize: '14px',
            lineHeight: '20px',
            fontWeight: 400,
            color: colors.gray.l45,
          }}
          data-test-id="company-name"
        >
          <EmailObfuscationWrapper>
            {project?.companyName}
          </EmailObfuscationWrapper>
        </Typography>
      </Box>

      <Divider style={{ borderColor: colors.gray.l85 }} />
      <Box display="flex" flexDirection="column" gap={1}>
        {!ndaDisplay.isNda && (
          <ProjectMetaData
            data-test-id="project-location"
            view={'homepage'}
            Icon={LocationIcon}
            items={projectAddressText}
            separator=", "
            visibleOn={['homepage']}
          />
        )}
        <ProjectMetaData
          data-test-id="project-duedate"
          view={'homepage'}
          Icon={CalendarIcon}
          suppressHydrationWarning
          items={
            status === 'closed'
              ? [I18n.t('components.projects.project_card.bidding_closed')]
              : project?.bidDueDate
              ? [
                  I18n.t('components.projects.project_card.bid_due_homepage', {
                    date: moment(project?.bidDueDate).format('MMM D, YYYY'),
                  }),
                ]
              : [
                  I18n.t(
                    'components.projects.project_card.bid_due_date_to_be_determined'
                  ),
                ]
          }
          separator=" "
          visibleOn={['homepage']}
        />
        <ProjectMetaData
          data-test-id="project-market-sector"
          view={'homepage'}
          Icon={BuildingIcon}
          items={formattedSector ? [formattedSector] : []}
          visibleOn={['homepage']}
        />
        <ProjectMetaData
          data-test-id="project-funding-type"
          view={'homepage'}
          Icon={PaymentsIcon}
          items={
            fundingType
              ? [capitalizeFirstLetterOfWords(fundingType), 'Funding']
              : []
          }
          separator=" "
          visibleOn={['homepage']}
        />
      </Box>
    </Container>
  );
};

export default ProjectCard;
